/** Form controls in the game's style: switch, volume slider, segmented choice. */
import type { ComponentChildren } from 'preact';
import { useEffect, useId, useState } from 'preact/hooks';
import { formatPercent } from '../../util/format';
import './controls.css';

export interface ToggleProps {
  label: string;
  icon?: ComponentChildren;
  checked: boolean;
  onChange(checked: boolean): void;
}

export function Toggle({ label, icon, checked, onChange }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      class={`toggle${checked ? ' toggle--on' : ''}`}
      onClick={() => onChange(!checked)}
    >
      {icon !== undefined && (
        <span class="control__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <span class="control__label">{label}</span>
      <span class="toggle__track" aria-hidden="true">
        <span class="toggle__thumb" />
      </span>
    </button>
  );
}

export interface SliderProps {
  label: string;
  icon?: ComponentChildren;
  /** 0..1 */
  value: number;
  onChange(value: number): void;
}

export function Slider({ label, icon, value, onChange }: SliderProps) {
  const id = useId();
  const percent = Math.round(value * 100);
  return (
    <div class="slider">
      <label class="slider__head" for={id}>
        {icon !== undefined && (
          <span class="control__icon" aria-hidden="true">
            {icon}
          </span>
        )}
        <span class="control__label">{label}</span>
        <span class="slider__value">{formatPercent(value)}</span>
      </label>
      <input
        id={id}
        class="slider__input"
        type="range"
        min={0}
        max={100}
        step={5}
        value={percent}
        style={{ '--fill': `${percent}%` }}
        onInput={(event) => onChange(Number(event.currentTarget.value) / 100)}
      />
    </div>
  );
}

export interface SegmentedOption<T extends string | number> {
  value: T;
  label: string;
  /** Emoji before the label; screen readers (and tests) get the plain label. */
  icon?: string;
}

export interface SegmentedProps<T extends string | number> {
  label: string;
  options: ReadonlyArray<SegmentedOption<T>>;
  value: T;
  onChange(value: T): void;
  /** Shows the choice without letting it change (the radios are disabled). */
  disabled?: boolean;
  /**
   * Long labels: two columns when the group is narrow (four options make a 2×2 grid), one row
   * where it is wide enough.
   */
  grid?: boolean;
}

/**
 * A tap is shown at once, even when the owner takes its time to say yes (a choice the server
 * confirms); if the owner never does, the choice goes back to its value after this long.
 */
const PENDING_MS = 1500;

/** One-of-N choice shown as a row of buttons (a native radio group underneath). */
export function Segmented<T extends string | number>({
  label,
  options,
  value,
  onChange,
  disabled = false,
  grid = false,
}: SegmentedProps<T>) {
  const group = useId();
  const [pending, setPending] = useState<{ value: T } | null>(null);
  useEffect(() => {
    if (!pending) return;
    if (pending.value === value) {
      setPending(null);
      return;
    }
    const timer = setTimeout(() => setPending(null), PENDING_MS);
    return () => clearTimeout(timer);
  }, [pending, value]);
  const shown = pending ? pending.value : value;
  return (
    <fieldset class={`segmented${grid ? ' segmented--grid' : ''}`} disabled={disabled}>
      <legend class="visually-hidden">{label}</legend>
      {options.map((option) => (
        <label
          key={option.value}
          class={[
            'segmented__option',
            option.icon !== undefined && 'segmented__option--icon',
            option.value === shown && 'segmented__option--on',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <input
            type="radio"
            class="visually-hidden segmented__input"
            name={group}
            checked={option.value === shown}
            // The fieldset disables them too; on each radio every browser and tool sees it.
            disabled={disabled}
            onChange={() => {
              setPending({ value: option.value });
              onChange(option.value);
            }}
          />
          {option.icon !== undefined && (
            <span class="segmented__icon" aria-hidden="true">
              {option.icon}
            </span>
          )}
          {option.label}
        </label>
      ))}
    </fieldset>
  );
}
